import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  BookOpen,
  Tent,
  HelpCircle,
  Quote,
  Phone,
  Megaphone,
  Home,
  Inbox,
  ArrowUpRight,
} from "lucide-react";
import { adminApi } from "../lib/adminApi";
import { useAdminAuth } from "./AdminAuthContext";
import { toast } from "sonner";

const CARDS = [
  {
    to: "/admin/inquiries",
    label: "Inquiries",
    icon: Inbox,
    desc: "Parent inquiries submitted from the public site.",
    countKey: "inquiries",
  },
  {
    to: "/admin/programs",
    label: "Programs",
    icon: BookOpen,
    desc: "Add, edit, hide, or reorder public program pages.",
    countKey: "programs",
  },
  {
    to: "/admin/camps",
    label: "Camps",
    icon: Tent,
    desc: "Manage camp listings — informational only.",
    countKey: "camps",
  },
  {
    to: "/admin/faqs",
    label: "FAQ",
    icon: HelpCircle,
    desc: "Edit parent questions and answers.",
    countKey: "faqs",
  },
  {
    to: "/admin/testimonials",
    label: "Testimonials",
    icon: Quote,
    desc: "Parent quotes shown publicly.",
    countKey: "testimonials",
  },
  {
    to: "/admin/contact-info",
    label: "Contact info",
    icon: Phone,
    desc: "Business name, phone, email, address, hours, social.",
    single: true,
  },
  {
    to: "/admin/homepage",
    label: "Homepage",
    icon: Home,
    desc: "Hero copy, Our Approach, Why-us cards, final CTA.",
    single: true,
  },
  {
    to: "/admin/announcements",
    label: "Announcements",
    icon: Megaphone,
    desc: "Banner shown at the top of every page when active.",
    countKey: "announcements",
  },
];

export default function AdminDashboard() {
  const { admin } = useAdminAuth();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [p, c, f, t, a, inq] = await Promise.all([
          adminApi.get("/admin/programs"),
          adminApi.get("/admin/camps"),
          adminApi.get("/admin/faqs"),
          adminApi.get("/admin/testimonials"),
          adminApi.get("/admin/announcements"),
          adminApi.get("/admin/inquiries"),
        ]);
        setCounts({
          programs: p.data.items.length,
          camps: c.data.items.length,
          faqs: f.data.items.length,
          testimonials: t.data.items.length,
          announcements: a.data.items.length,
          inquiries: (inq.data || []).length,
        });
      } catch {
        toast.error("Couldn't load dashboard stats — refresh to retry.");
      }
    };
    load();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
        Welcome back, {admin?.email?.split("@")[0]}.
      </h1>
      <p className="mt-2 text-sm text-slate-500 max-w-2xl leading-relaxed">
        Manage everything visible on the public Nexora website. Changes are
        live as soon as you save them.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="group rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-md transition-all hover:-translate-y-0.5"
            data-testid={`admin-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-start justify-between gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <c.icon className="h-5 w-5" />
              </span>
              <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900" />
            </div>
            <p className="mt-5 font-display text-lg font-bold tracking-tight text-slate-900">
              {c.label}
              {!c.single && counts[c.countKey] != null && (
                <span className="ml-2 text-xs text-slate-400 font-bold">
                  · {counts[c.countKey]}
                </span>
              )}
            </p>
            <p className="mt-1 text-sm text-slate-500 leading-relaxed">{c.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
